/**
 * UserDashboard permissions — keyed by existing Customer `role` values.
 * Uses the same access for `user` and `admin` (no separate persona matrix).
 * Spotless staff (`AdminController`, `Hr`, …) are not hospital roles; they use
 * admin portal + support `adminView` paths, not this Customer RBAC table.
 */

/** Customer account roles already used in auth / User model. */
export const HOSPITAL_ROLES = ['user', 'admin'];

/** Legacy persona values that may still appear on older JWTs / member rows. */
const LEGACY_PERSONA_ROLES = [
  'IPC_LEAD',
  'QUALITY',
  'EVS_SUP',
  'EVS_STAFF',
  'OPERATOR',
  'ADMIN',
];

const ROLE_SET = new Set([...HOSPITAL_ROLES, ...LEGACY_PERSONA_ROLES]);

/** Full UserDashboard access — shared by `user` and `admin`. */
const FULL_DASHBOARD_PERMISSIONS = [
  'nav:overview',
  'nav:reports',
  'nav:operations',
  'nav:rooms',
  'nav:efficacy',
  'nav:subscription',
  'nav:robot',
  'nav:members',
  'reports:read',
  'reports:write',
  'reports:export',
  'exceptions:read',
  'exceptions:write',
  'followups:read',
  'followups:write',
  'manual_clean:write',
  'verification:write',
  'compliance:read',
  'compliance:write',
  'rooms:master',
  'rooms:assign',
  'rooms:today',
  'rooms:readiness',
  'operations:write',
  'subscription:write',
  'members:write',
];

/** @type {Record<string, string[]>} */
const ROLE_PERMISSIONS = {
  user: FULL_DASHBOARD_PERMISSIONS,
  admin: FULL_DASHBOARD_PERMISSIONS,
};

/**
 * Normalize Customer role for permission checks.
 * Maps legacy personas → `admin` (same access). Does not treat AdminController as hospital.
 */
export function normalizeHospitalRole(role) {
  const r = String(role ?? '').trim();
  const lower = r.toLowerCase();
  if (!r || lower === 'user') return 'user';
  if (lower === 'admin') return 'admin';
  if (LEGACY_PERSONA_ROLES.includes(r)) return 'admin';
  if (ROLE_SET.has(r)) return lower === 'user' ? 'user' : 'admin';
  return 'user';
}

export function isHospitalRole(role) {
  const r = String(role ?? '').trim();
  const lower = r.toLowerCase();
  if (lower === 'user' || lower === 'admin') return true;
  return LEGACY_PERSONA_ROLES.includes(r);
}

export function getPermissionsForRole(role) {
  return ROLE_PERMISSIONS[normalizeHospitalRole(role)] || ROLE_PERMISSIONS.user;
}

export function can(role, permission) {
  const perms = getPermissionsForRole(role);
  if (perms.includes('*')) return true;
  if (perms.includes(permission)) return true;
  const [resource] = String(permission).split(':');
  return perms.includes(`${resource}:*`);
}

export function canAny(role, permissions = []) {
  return permissions.some((p) => can(role, p));
}

/** Default post-login / unauthorized redirect inside UserDashboard. */
export function defaultLandingPath(role) {
  void role;
  return '/client/overview';
}

/** Nav `sub` / clientPath → required permission */
export const NAV_PERMISSION_BY_KEY = {
  overview: 'nav:overview',
  reports: 'nav:reports',
  operations: 'nav:operations',
  rooms: 'nav:rooms',
  efficacy: 'nav:efficacy',
  subscription: 'nav:subscription',
  robot: 'nav:robot',
  members: 'nav:members',
};

export function hospitalRoleLabel(role) {
  const r = normalizeHospitalRole(role);
  const labels = {
    user: 'User',
    admin: 'Admin',
  };
  return labels[r] || r;
}
